import type { ComponentPropsWithoutRef, ElementRef } from "react";
import { forwardRef } from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "@/lib/cn";

/**
 * Thin Radix-based Tooltip primitives.
 *
 * Usage:
 *   <TooltipProvider>
 *     <Tooltip>
 *       <TooltipTrigger asChild>
 *         <button>Hover me</button>
 *       </TooltipTrigger>
 *       <TooltipContent>Full text</TooltipContent>
 *     </Tooltip>
 *   </TooltipProvider>
 */
export const TooltipProvider = TooltipPrimitive.Provider;
export const Tooltip = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;

export const TooltipContent = forwardRef<
  ElementRef<typeof TooltipPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        /*
          BUG-645. `z-[9999]`, not `z-50`.

          This content is PORTALLED to the end of `<body>`, so it shares one
          stacking context with every other portalled layer and competes with
          them on the raw number. At `z-50` it lost to all of them: the schedule
          modal is `ZClass.modal` = `z-[60]`, `RoutesFilterModal` and the orders
          filter panel are `z-[70]`, and `SaveFilterDialog` / an elevated
          `ConfirmDialog` are `z-[100]`. A hint opened inside any of those
          rendered BEHIND the panel — mounted, positioned, carrying its text,
          and invisible.

          That stayed hidden while hint text was also printed in the form body:
          the `i` looked decorative, so nobody missed it. TASK-5071 moved the
          text under the `i` to buy back modal height, which made the tooltip
          the ONLY way to read it and turned a cosmetic defect into lost
          content («и ну i нет подсказок»).

          `z-[9999]` is the existing convention for portalled layers that must
          outrank everything (`VehicleFilterDropdown`, `DriverFilterDropdown`,
          `UnassignedOrdersSlideOver`). A tooltip is the right occupant: it is
          transient, non-modal, never covers a decision it did not trigger, and
          is always dismissed before anything beneath it is acted on.

          Do NOT "fix" a future recurrence by raising the individual caller —
          the number lives here so all ~15 consumers share one answer.
        */
        "z-[9999] rounded-md bg-gray-900 px-2.5 py-1.5 text-xs leading-snug text-white shadow-md",
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;
